// Temps de réponse d'un site surveillé : moyenne par créneau (ligne), plage
// min–max (bande), créneaux avec échecs (barres rouges au pied du graphique).
// SVG étiré sur la largeur (`preserveAspectRatio="none"`, traits non mis à
// l'échelle) ; les graduations sont en HTML pour garder un texte net.

import { useMemo, useRef, useState } from 'react';

const W = 1000;
const H = 160;

function niceMax(v) {
  if (!v || v <= 0) return 100;
  const steps = [100, 200, 250, 500, 750, 1000, 1500, 2000, 2500, 3000, 5000, 7500, 10000, 15000, 20000, 30000, 60000];
  return steps.find((s) => s >= v * 1.1) || Math.ceil((v * 1.1) / 10000) * 10000;
}

const fmtMs = (v) => (v >= 10000 ? (v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' s' : v + ' ms');

export function ResponseTimeChart({ series }) {
  const wrap = useRef(null);
  const [hover, setHover] = useState(null);
  const points = series?.points || [];
  const n = points.length;

  const { max, avgPath, band } = useMemo(() => {
    const top = niceMax(Math.max(0, ...points.map((p) => p.max ?? p.avg ?? 0)));
    const x = (i) => (n <= 1 ? W / 2 : (i / (n - 1)) * W);
    const y = (v) => H - (v / top) * H;
    // Ligne coupée sur les créneaux sans mesure : on n'invente pas de pente.
    // Un point isolé (voisins sans mesure) devient un court trait horizontal :
    // un « M » seul ne dessine rien, et un site tout juste ajouté n'a qu'un point.
    const half = n <= 1 ? W / 8 : Math.max(W / (n - 1) / 2, 4);
    let d = '';
    points.forEach((p, i) => {
      if (p.avg == null) return;
      const prev = i > 0 && points[i - 1].avg != null;
      const next = i < n - 1 && points[i + 1].avg != null;
      const px = x(i);
      const py = y(p.avg).toFixed(1);
      if (!prev && !next) d += `M${(px - half).toFixed(1)},${py}L${(px + half).toFixed(1)},${py}`;
      else d += `${prev ? 'L' : 'M'}${px.toFixed(1)},${py}`;
    });
    // Bande min–max, par segments continus.
    const polys = [];
    let run = [];
    const flush = () => {
      if (run.length > 1) {
        const upper = run.map((i) => `${x(i).toFixed(1)},${y(points[i].max).toFixed(1)}`);
        const lower = [...run].reverse().map((i) => `${x(i).toFixed(1)},${y(points[i].min).toFixed(1)}`);
        polys.push(upper.concat(lower).join(' '));
      }
      run = [];
    };
    points.forEach((p, i) => (p.avg == null ? flush() : run.push(i)));
    flush();
    return { max: top, avgPath: d, band: polys };
  }, [points, n]);

  if (!series) return <div className="h-[200px] flex items-center justify-center text-muted-2 text-xs">Chargement…</div>;
  if (series.checks === 0)
    return (
      <div className="h-[200px] flex items-center justify-center text-muted-2 text-xs">
        Aucune vérification sur cette période.
      </div>
    );

  const longRange = series.hours > 24;
  const fmtAt = (d) =>
    new Date(d).toLocaleString('fr-FR', longRange ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' } : { hour: '2-digit', minute: '2-digit' });
  const slot = W / Math.max(1, n);

  const onMove = (e) => {
    const r = wrap.current?.getBoundingClientRect();
    if (!r || n === 0) return;
    const i = Math.round(((e.clientX - r.left) / r.width) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const hp = hover != null ? points[hover] : null;

  return (
    <div className="flex gap-2">
      <div className="flex flex-col justify-between text-[10px] text-muted-2 font-mono text-right w-12 h-[160px] -mt-1.5">
        <span>{fmtMs(max)}</span>
        <span>{fmtMs(max / 2)}</span>
        <span>0 ms</span>
      </div>
      <div className="flex-1 min-w-0">
        <div
          ref={wrap}
          className="relative h-[160px] cursor-crosshair"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
            {[0, 0.5, 1].map((f) => (
              <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} className="stroke-rule" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {points.map((p, i) =>
              p.failures > 0 ? (
                <rect
                  key={'f' + i}
                  x={n <= 1 ? 0 : (i / (n - 1)) * W - slot / 2}
                  width={Math.max(slot, 2)}
                  y={0}
                  height={H}
                  className={p.failures >= p.checks ? 'fill-crit/25' : 'fill-crit/10'}
                />
              ) : null
            )}
            {band.map((pts, i) => (
              <polygon key={i} points={pts} className="fill-blue-500/15" />
            ))}
            <path d={avgPath} fill="none" className="stroke-blue-400" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
            {hover != null && (
              <line
                x1={n <= 1 ? W / 2 : (hover / (n - 1)) * W}
                x2={n <= 1 ? W / 2 : (hover / (n - 1)) * W}
                y1="0"
                y2={H}
                className="stroke-muted-2"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {hp && (
            <div
              className="absolute top-1 z-10 bg-sidebar border border-rule-2 rounded-lg px-2.5 py-1.5 text-[11px] text-fg-2 shadow-lg pointer-events-none whitespace-nowrap"
              style={hover / Math.max(1, n - 1) > 0.6 ? { right: `${(1 - hover / Math.max(1, n - 1)) * 100 + 1}%` } : { left: `${(hover / Math.max(1, n - 1)) * 100 + 1}%` }}
            >
              <div className="text-muted-2 mb-0.5">{fmtAt(hp.at)}</div>
              {hp.avg != null ? (
                <div className="font-mono">
                  moy. {fmtMs(hp.avg)}
                  {hp.min !== hp.max && (
                    <span className="text-muted-2">
                      {' '}
                      ({fmtMs(hp.min)} – {fmtMs(hp.max)})
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-muted-2">{hp.checks === 0 ? 'aucune vérification' : 'aucune réponse'}</div>
              )}
              {hp.failures > 0 && (
                <div className="text-crit">
                  {hp.failures} échec{hp.failures > 1 ? 's' : ''} sur {hp.checks}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-between text-[10px] text-muted-2 font-mono mt-1">
          <span>{n > 0 && fmtAt(points[0].at)}</span>
          <span>{n > 2 && fmtAt(points[Math.floor(n / 2)].at)}</span>
          <span>maintenant</span>
        </div>
      </div>
    </div>
  );
}
